/**
 * Queue Filters Components - Search, filter toggles and filter panels
 */
import { Plus, X } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "../../../components/ui/tabs";

// Search Input Component
export const SearchInput = ({ value, onChange, onClear, placeholder, testId }) => (
  <div className="relative">
    <input 
      type="text" 
      placeholder={placeholder || "Pretraži po nazivu ili ID broju..."} 
      value={value} 
      onChange={(e) => onChange(e.target.value)} 
      data-testid={testId}
      className="w-full h-10 pl-10 pr-10 bg-gray-50 border border-gray-200 rounded-lg text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1B3F8F]/20 focus:border-[#1B3F8F]" 
    />
    <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
    {value && (
      <button onClick={onClear} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
        <X size={16} />
      </button>
    )}
  </div>
);

// Filter Toggle Button
export const FilterToggle = ({ showFilters, hasActiveFilters, activeCount, onToggle, onClear }) => (
  <div className="flex items-center justify-between mt-3 lg:mt-0">
    <button 
      onClick={onToggle} 
      className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg transition-colors ${showFilters || hasActiveFilters ? "bg-[#1B3F8F] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`} 
      data-testid="toggle-filters-button"
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
      </svg>
      Filteri
      {hasActiveFilters && (
        <span className="w-5 h-5 bg-white text-[#1B3F8F] rounded-full text-xs flex items-center justify-center font-medium">
          {activeCount}
        </span>
      )}
    </button>
    {hasActiveFilters && (
      <button onClick={onClear} className="text-sm text-gray-500 hover:text-[#1B3F8F]">
        Poništi sve filtere
      </button>
    )}
  </div>
);

// Desktop Filter Panel
export const DesktopFilterPanel = ({
  cityFilter, setCityFilter,
  industryFilter, setIndustryFilter,
  nextActionFilter, setNextActionFilter,
  agentFilter, setAgentFilter,
  sourceFilter, setSourceFilter,
  targetListId, setTargetListId,
  uniqueCities, uniqueIndustries, agents, sources, targetLists,
  canFilterByAgent, isTeamLeader,
  onFilterChange
}) => (
  <div className="mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Grad</label>
        <select 
          value={cityFilter} 
          onChange={(e) => { setCityFilter(e.target.value); onFilterChange(); }} 
          data-testid="filter-city" 
          className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1B3F8F]/20"
        >
          <option value="">Svi gradovi</option>
          {uniqueCities.map(city => <option key={city} value={city}>{city}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Industrija</label>
        <select 
          value={industryFilter} 
          onChange={(e) => { setIndustryFilter(e.target.value); onFilterChange(); }} 
          data-testid="filter-industry" 
          className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1B3F8F]/20"
        >
          <option value="">Sve industrije</option>
          {uniqueIndustries.map(industry => <option key={industry} value={industry}>{industry}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Sljedeće</label>
        <select 
          value={nextActionFilter} 
          onChange={(e) => { setNextActionFilter(e.target.value); onFilterChange(); }} 
          data-testid="filter-next-action" 
          className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1B3F8F]/20"
        >
          <option value="">Sve</option>
          <option value="today">Danas</option>
          <option value="overdue">Zakašnjelo</option>
          <option value="this_week">Ovaj tjedan</option>
          <option value="no_date">Bez datuma</option>
        </select>
      </div>
      {canFilterByAgent && (
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">
            {isTeamLeader ? "Agent u timu" : "Dodijeljeno agentu"}
          </label>
          <select 
            value={agentFilter} 
            onChange={(e) => { setAgentFilter(e.target.value); onFilterChange(); }} 
            data-testid="filter-agent" 
            className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1B3F8F]/20"
          >
            <option value="">Svi agenti</option>
            {agents.map(agent => <option key={agent.id} value={agent.id}>{agent.name}</option>)}
          </select>
        </div>
      )}
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Izvor</label>
        <select 
          value={sourceFilter} 
          onChange={(e) => { setSourceFilter(e.target.value); onFilterChange(); }} 
          data-testid="filter-source" 
          className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1B3F8F]/20"
        >
          <option value="">Svi izvori</option>
          {(sources || []).map(s => (
            <option key={s.name} value={s.name}>
              {s.count != null ? `${s.name} (${s.count})` : s.name}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Ciljna lista</label>
        <select
          value={targetListId || ""}
          onChange={(e) => { setTargetListId(e.target.value); onFilterChange(); }}
          data-testid="filter-target-list"
          className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1B3F8F]/20"
        >
          <option value="">Sve ciljne liste</option>
          {(targetLists || []).map(tl => (
            <option key={tl.id} value={tl.id}>
              {tl.member_count != null ? `${tl.name} (${tl.member_count})` : tl.name}
            </option>
          ))}
        </select>
      </div>
    </div>
  </div>
);

// Mobile Filter Panel
export const MobileFilterPanel = ({
  cityFilter, setCityFilter,
  industryFilter, setIndustryFilter,
  nextActionFilter, setNextActionFilter,
  agentFilter, setAgentFilter,
  sourceFilter, setSourceFilter,
  targetListId, setTargetListId,
  uniqueCities, uniqueIndustries, agents, sources, targetLists,
  canFilterByAgent, isTeamLeader,
  onFilterChange
}) => (
  <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-2">
    <select 
      value={cityFilter} 
      onChange={(e) => { setCityFilter(e.target.value); onFilterChange(); }} 
      data-testid="filter-city-mobile" 
      className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1B3F8F]/20"
    >
      <option value="">Svi gradovi</option>
      {uniqueCities.map(city => <option key={city} value={city}>{city}</option>)}
    </select>
    <select 
      value={industryFilter} 
      onChange={(e) => { setIndustryFilter(e.target.value); onFilterChange(); }} 
      data-testid="filter-industry-mobile" 
      className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1B3F8F]/20"
    >
      <option value="">Sve industrije</option>
      {uniqueIndustries.map(industry => <option key={industry} value={industry}>{industry}</option>)}
    </select>
    <select 
      value={nextActionFilter} 
      onChange={(e) => { setNextActionFilter(e.target.value); onFilterChange(); }} 
      data-testid="filter-next-action-mobile" 
      className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1B3F8F]/20"
    >
      <option value="">Sljedeće: Sve</option>
      <option value="today">Danas</option>
      <option value="overdue">Zakašnjelo</option>
      <option value="this_week">Ovaj tjedan</option>
      <option value="no_date">Bez datuma</option>
    </select>
    {canFilterByAgent && (
      <select 
        value={agentFilter} 
        onChange={(e) => { setAgentFilter(e.target.value); onFilterChange(); }} 
        data-testid="filter-agent-mobile" 
        className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1B3F8F]/20"
      >
        <option value="">{isTeamLeader ? "Svi agenti u timu" : "Svi agenti"}</option>
        {agents.map(agent => <option key={agent.id} value={agent.id}>{agent.name}</option>)}
      </select>
    )}
    <select 
      value={sourceFilter} 
      onChange={(e) => { setSourceFilter(e.target.value); onFilterChange(); }} 
      data-testid="filter-source-mobile" 
      className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1B3F8F]/20"
    >
      <option value="">Svi izvori</option>
      {(sources || []).map(s => (
        <option key={s.name} value={s.name}>
          {s.count != null ? `${s.name} (${s.count})` : s.name}
        </option>
      ))}
    </select>
    <select
      value={targetListId || ""}
      onChange={(e) => { setTargetListId(e.target.value); onFilterChange(); }}
      data-testid="filter-target-list-mobile"
      className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1B3F8F]/20"
    >
      <option value="">Sve ciljne liste</option>
      {(targetLists || []).map(tl => (
        <option key={tl.id} value={tl.id}>
          {tl.member_count != null ? `${tl.name} (${tl.member_count})` : tl.name}
        </option>
      ))}
    </select>
  </div>
);

// Queue Tabs Component
export const QueueTabs = ({ activeTab, onTabChange, isMobile }) => (
  <Tabs value={activeTab} onValueChange={onTabChange} className={isMobile ? "flex-1" : ""}>
    <TabsList 
      className={`bg-gray-100 p-1 rounded-lg grid grid-cols-2 ${isMobile ? "w-full max-w-[200px]" : "w-full max-w-xs"}`} 
      data-testid={isMobile ? "company-tabs-mobile" : "company-tabs"}
    >
      <TabsTrigger 
        value="active" 
        className="data-[state=active]:bg-white data-[state=active]:text-[#1B3F8F] data-[state=active]:shadow-sm py-2 rounded-md text-sm font-medium text-gray-500 transition-all" 
        data-testid={isMobile ? "tab-active-mobile" : "tab-active"}
      >
        Aktivni
      </TabsTrigger>
      <TabsTrigger 
        value="all" 
        className="data-[state=active]:bg-white data-[state=active]:text-[#1B3F8F] data-[state=active]:shadow-sm py-2 rounded-md text-sm font-medium text-gray-500 transition-all" 
        data-testid={isMobile ? "tab-all-mobile" : "tab-all"}
      >
        Sve
      </TabsTrigger>
    </TabsList>
  </Tabs>
);

// Add Company Button
export const AddCompanyButton = ({ onClick, isMobile }) => {
  if (isMobile) {
    return (
      <button 
        onClick={onClick} 
        data-testid="add-company-button-mobile" 
        className="flex items-center justify-center w-10 h-10 bg-[#1B3F8F] text-white rounded-lg hover:bg-[#152f6b] transition-colors ml-3"
      >
        <Plus size={20} />
      </button>
    );
  }
  
  return (
    <button 
      onClick={onClick} 
      data-testid="add-company-button-desktop" 
      className="flex items-center gap-2 px-4 py-2 bg-[#1B3F8F] text-white rounded-lg text-sm font-medium hover:bg-[#152f6b] transition-colors"
    >
      <Plus size={18} />
      <span>Nova firma</span>
    </button>
  );
};
